import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, CircularProgress, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { fetchDropdownData, fetchRealignmentRecords } from "../../../redux/realignmentSlice"; 
import DataTable from "../../../Components/Common/datatable/DataTable";
import ServerTablePaginationActions from "../../../Components/Common/pagination/ServerTablePaginationActions";
import { useServerPagination } from "../../../utils/useServerPagination";

const ActionHistoryList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef();
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Redux state
  const {
    pendingRecords,
    dropdownData,
    loading,
    totalElements,
    mdApprovalStatus,
  } = useSelector((state) => state.realignment);

  const [filters, setFilters] = useState({
    location: [],
    region: [],
    kit: [],
  });
  const { paginationModel, setPage, setPageSize } = useServerPagination({
    initialPage: 0,
    initialPageSize: 10,
  });

  const statusQuery = useMemo(() => {
    if (statusFilter === "ALL") {
      return "APPROVED,REJECTED";
    }
    return statusFilter;
  }, [statusFilter]);

  // Fetch dropdown data on component mount
  useEffect(() => {
    dispatch(fetchDropdownData());
  }, [dispatch]);

  // Fetch records whenever server pagination model changes
  useEffect(() => {
    setPage(0);
  }, [setPage, statusFilter]);

  useEffect(() => {
    dispatch(
      fetchRealignmentRecords({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        status: statusQuery,
      }),
    );
  }, [dispatch, paginationModel.page, paginationModel.pageSize, statusQuery]);

  // Dropdown options from API
  const locationOptions = dropdownData.locations || [];
  const profitCenterOptions = dropdownData.profitCenters || [];
  const forecastPartnerOptions = dropdownData.forecastPartners || [];

  const filteredRecords = useMemo(() => pendingRecords || [], [pendingRecords]);

  // Helper function to get location name by ID with concatenation
  const getLocationNameById = (locationId) => {
    if (!locationId) return "-";
    const location = locationOptions.find((loc) => loc.locationId === locationId);
    return location ? `${location.locationId}-${location.location}` : locationId;
  };

  // Helper function to get profit center description by ID with concatenation
  const getProfitCenterDescById = (profitCenterId) => {
    if (!profitCenterId) return "-";
    const pc = profitCenterOptions.find((p) => p.profitCenterId === profitCenterId);
    return pc ? `${pc.profitCenterId}-${pc.profitCenterDescription}` : profitCenterId;
  };

  // Helper function to get forecast partner description by ID with concatenation
  const getForecastPartnerDescById = (customerId) => {
    if (!customerId) return "-";
    const fp = forecastPartnerOptions.find((f) => f.customerId === customerId);
    return fp ? `${fp.customerId}-${fp.customerDescription}` : customerId;
  };

  // Status renderer
  const StatusRenderer = (params) => {
    const status = params.data.realignmentStatus || "PENDING";
    const statusColor = status === "APPROVED" ? "#4caf50" : status === "REJECTED" ? "#f44336" : "#ff9800";
    
    return (
      <span style={{ color: statusColor, fontWeight: "bold" }}>
        {status}
      </span>
    );
  };

  // Mass Data Status renderer
 const MasterDataStatusRenderer = (params) => {
    const isMdApproved = mdApprovalStatus[params.data.id] === true;
    const masterDataStatus = params.data.masterDataApprovalStatus || "PENDING";

    const statusColor =
      masterDataStatus === "APPROVED"
        ? "#4caf50"
        : masterDataStatus === "REJECTED"
          ? "#f44336"
          : "#ff9800";

    if (isMdApproved) {
      return (
        <span style={{ color: statusColor, fontWeight: "bold" }}>{masterDataStatus}</span>
      );
    }
    return <span style={{ color: statusColor, fontWeight: "bold" }}>{masterDataStatus}</span>;
  };

  const commonColumns = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      field: "supportCheckbox",
      headerName: "",
      sortable: false,
      width: 25,
      suppressSizeToFit: true,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "flowProjectId",
      headerName: "Project ID",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => (
        <Tooltip title={params.data.flowProjectId} arrow>
          <span>{params.data.flowProjectId}</span>
        </Tooltip>
      ),
      cellStyle: { textAlign: "right" },
    },
    {
      field: "oldKit",
      headerName: "Old Kit",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 150,
      cellRenderer: (params) => {
      const oldKit = params?.data?.oldKit ?? "";
      const oldKitDescription = params?.data?.oldPrdDesc ?? "";
      const displayValue = oldKit || oldKitDescription
        ? `${oldKit}${oldKit && oldKitDescription ? "-" : ""}${oldKitDescription}`
        : "-";
      
      return <Tooltip title={displayValue} arrow>
        <span>{displayValue}</span>
      </Tooltip>
      },
      cellStyle: { textAlign: "left" },
    },
    {
      field: "location",
      headerName: "Original Location",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => {
        const locationName = getLocationNameById(params.data.location);
        return (
          <Tooltip title={locationName} arrow>
            <span>{locationName}</span>
          </Tooltip>
        );
      },
      cellStyle: { textAlign: "left" },
    },
    {
      field: "countryOfSales",
      headerName: "Country of Sales",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.countryOfSales || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "region",
      headerName: "Region",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.region || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
        field: "projectType",
        headerName: "Project Type",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const projectType = params.data.projectType || "";
          const projectTypeDesc = params.data.projectTypeDesc || "";
          const displayValue = projectType
            ? projectTypeDesc
              ? `${projectType} - ${projectTypeDesc}`
              : projectType
            : "-";

          return <span>{displayValue}</span>;
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "oldProfitCenter",
        headerName: "Original Profit Center",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const oldProfitCenter = params?.data?.oldProfitCenter ?? "";
          const oldProfitCenterDescription = params?.data?.oldProfitCenterDescription ?? "";
          const displayValue = oldProfitCenter || oldProfitCenterDescription
            ? `${oldProfitCenter}${oldProfitCenter && oldProfitCenterDescription ? "-" : ""}${oldProfitCenterDescription}`
            : "-";

          return <span>{displayValue}</span>;
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "oldForecastPartner",
        headerName: "Original Forecast Partner",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const oldForecastPartner = params?.data?.oldForecastPartner ?? "";
          const oldForecastPartnerDescription = params?.data?.oldForecastPartnerDescription ?? "";
          const displayValue = oldForecastPartner || oldForecastPartnerDescription
            ? `${oldForecastPartner}${oldForecastPartner && oldForecastPartnerDescription ? "-" : ""}${oldForecastPartnerDescription}`
            : "-";

          return <span>{displayValue}</span>;
        },
        cellStyle: { textAlign: "left" },
      },
    {
      field: "beverageProductDescription",
      headerName: "Beverage Product Description",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 225,
      cellRenderer: (params) => {
        const beverageProductId = params?.data?.beverageProductId ?? "";
        const beverageProductDescription = params?.data?.beverageProductDescription ?? "";
        const displayValue = beverageProductId || beverageProductDescription
          ? `${beverageProductId}${beverageProductId && beverageProductDescription ? "-" : ""}${beverageProductDescription}`
          : "-";
        return (
          <Tooltip title={displayValue} arrow>
            <span>{displayValue}</span>
          </Tooltip>
        );
      },
      cellStyle: { textAlign: "left" },
    },
    {
      field: "ibpProjectName",
      headerName: "IBP Project Name",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.ibpProjectName || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "newKit",
      headerName: "New Kit",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 150,
      cellRenderer: (params) => {
      const newKit = params?.data?.newKit ?? "";
      const newKitDescription = params?.data?.newPrdDesc ?? "";
      const displayValue = newKit || newKitDescription
        ? `${newKit}${newKit && newKitDescription ? "-" : ""}${newKitDescription}`
        : "-";
      
      return <Tooltip title={displayValue} arrow>
        <span>{displayValue}</span>
      </Tooltip>
      },
      cellStyle: { textAlign: "left" },
    },
    {
      field: "newLocation",
      headerName: "Adjusted Location",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 200,
      cellRenderer: (params) => (
        <span>{getLocationNameById(params.data.newLocation)}</span>
      ),
      cellStyle: { textAlign: "left" },
    },
    {
      field: "newProfitCenter",
      headerName: "Adjusted Profit Center",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 225,
      cellRenderer: (params) => (
        <span>{getProfitCenterDescById(params.data.newProfitCenter)}</span>
      ),
      cellStyle: { textAlign: "left" },
    },
    {
      field: "newForecastPartner",
      headerName: "Adjusted Forecast Partner",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 225,
      cellRenderer: (params) => (
        <span>{getForecastPartnerDescById(params.data.newForecastPartner)}</span>
      ),
      cellStyle: { textAlign: "left" },
    },
    {
      field: "sourceKeyFigurePercentage",
      headerName: "Source Key Figure %",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 150,
      cellRenderer: (params) => <span>{params.data.sourceKeyFigurePercentage || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "masterDataApprovalStatus",
      headerName: "Master Data Status",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 200,
      cellRenderer: MasterDataStatusRenderer,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "status",
      headerName: "Realignment Status",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 200,
      cellRenderer: StatusRenderer,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "masterDataApprovalDate",
      headerName: "Master Data Approval Date",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 225,
      cellRenderer: (params) => <span>{params.data.masterDataApprovalDate || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "realignmentApprovedDate",
      headerName: "Realignment Approval Date",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 225,
      cellRenderer: (params) => <span>{params.data.realignmentApprovedDate || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
          field: "mdApprovedBy",
          headerName: "MD Approved By",
          sortable: true,
          unSortIcon: true,
          suppressHeaderMenuButton: true,
          filterParams: {
            filterOptions: ["contains", "startsWith", "endsWith"],
            defaultOption: "contains",
            buttons: ["apply", "reset"],
          },
          filter: "agTextColumnFilter",
          width: 200,
          cellRenderer: (params) => {
            const displayValue = params.data.mdApprovedBy || "-";
            return (
              <Tooltip title={displayValue} arrow>
                <span>{displayValue}</span>
              </Tooltip>
            );
          },
          cellStyle: { textAlign: "left" },
        },
        {
          field: "reApprovedBy",
          headerName: "Realignment Approved By",
          sortable: true,
          unSortIcon: true,
          suppressHeaderMenuButton: true,
          filterParams: {
            filterOptions: ["contains", "startsWith", "endsWith"],
            defaultOption: "contains",
            buttons: ["apply", "reset"],
          },
          filter: "agTextColumnFilter",
          width: 200,
          cellRenderer: (params) => {
            const displayValue = params.data.reApprovedBy || "-";
            return (
              <Tooltip title={displayValue} arrow>
                <span>{displayValue}</span>
              </Tooltip>
            );
          },
          cellStyle: { textAlign: "left" },
        },
    {
      field: "createdAt",
      headerName: "Data Created in AL",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.createdAt || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.updatedAt || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "rejectionRemarks",
      headerName: "Remarks",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 200,
      cellRenderer: (params) => <span>{params.data.rejectionRemarks || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
  ], [locationOptions, profitCenterOptions, forecastPartnerOptions, mdApprovalStatus]);

  const pagination = false;
  const paginationPageSize = paginationModel.pageSize;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.autoSizeAllColumns(false);
    }
  };

  const onPageChange = (_, nextPage) => {
    setPage(nextPage);
  };

  const onRowsPerPageChange = (event) => {
    const nextSize = Number(event.target.value);
    setPageSize(nextSize);
  };

  const locationFilterOptions = useMemo(
    () => {
      const locationIds = [...new Set(filteredRecords.map(r => r.location).filter(Boolean))];
      return locationIds.map(locId => ({
        value: locId,
        label: `${locId}-${locationOptions.find(l => l.locationId === locId)?.location || locId}`
      }));
    },
    [filteredRecords, locationOptions]
  );
  
  const regionFilterOptions = useMemo(
    () => [...new Set(filteredRecords.map(r => r.region).filter(Boolean))],
    [filteredRecords]
  );
  
  const kitFilterOptions = useMemo(
    () => {
      const kitCodes = [...new Set(filteredRecords.map(r => r.newKit).filter(Boolean))];
      return kitCodes.map(kit => {
        const kitRecord = filteredRecords.find(r => r.newKit === kit);
        return {
          value: kit,
          label: `${kit}${kitRecord?.newPrdDesc ? '-' + kitRecord.newPrdDesc : ''}`
        };
      });
    },
    [filteredRecords]
  );

  const filteredRecordsData = useMemo(() => {
    const locationValues = filters.location.map(item => item.value);
    const kitValues = filters.kit.map(item => item.value);
    return filteredRecords.filter((r) =>
      (locationValues.length === 0 || locationValues.includes(r.location)) &&
      (filters.region.length === 0 || filters.region.includes(r.region)) &&
      (kitValues.length === 0 || kitValues.includes(r.newKit))
    );
  }, [filteredRecords, filters]);
  
  const clearFilterVisibility = filters.location.length > 0 || filters.region.length > 0 || filters.kit.length > 0;

  return (
    <div>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, justifyContent: "space-between" }}>
          {/* Filter Inputs */}
          <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
            <Box sx={{ display: "flex", gap: 2}}><Typography style={{fontSize:"14px", padding: "6px 8px"}}>Filter Options: </Typography>
            {clearFilterVisibility && (
              <Button
                variant="text"
                onClick={() => setFilters({ location: [], region: [], kit: [] })}
              >
                Clear Filters
              </Button>
            )}
            </Box>
            <Box sx={{ display: "flex", gap: 1}}>
              <Autocomplete
              multiple
              size="small"
              options={locationFilterOptions}
              value={filters.location}
              onChange={(_, v) => setFilters(f => ({ ...f, location: v }))}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              sx={{ width: 250 }}
              renderInput={(p) => <TextField {...p} label="Location" placeholder="Select locations" />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <input type="checkbox" checked={selected} readOnly style={{ marginRight: "8px" }} />
                  {option.label}
                </li>
              )}
              disableCloseOnSelect
            />
      
            <Autocomplete
              multiple
              size="small"
              options={regionFilterOptions}
              value={filters.region}
              onChange={(_, v) => setFilters(f => ({ ...f, region: v }))}
              sx={{ width: 250 }}
              renderInput={(p) => <TextField {...p} label="Region" placeholder="Select regions" />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <input type="checkbox" checked={selected} readOnly style={{ marginRight: "8px" }} />
                  {option}
                </li>
              )}
              disableCloseOnSelect
            />
      
            <Autocomplete
              multiple
              size="small"
              options={kitFilterOptions}
              value={filters.kit}
              onChange={(_, v) => setFilters(f => ({ ...f, kit: v }))}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              sx={{ width: 250 }}
              renderInput={(p) => <TextField {...p} label="New Kit" placeholder="Select kits" />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <input type="checkbox" checked={selected} readOnly style={{ marginRight: "8px" }} />
                  {option.label}
                </li>
              )}
              disableCloseOnSelect
            />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
          <label style={{fontSize:"14px", padding: "6px 8px"}}>Realignment Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "6px 12px",
              fontSize: "14px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              cursor: "pointer",
            }}
          >
            <option value="ALL">All</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <DataTable
            ref={gridRef}
            columns={commonColumns}
            rows={filteredRecordsData || []}
            tableHeight={"70vh"}
            suppressMovableColumns={true}
            suppressHorizontalScroll={false}
            suppressPaginationPanel={true}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            onGridReady={onGrid}
            domLayout="normal"
            rowStyle={(params) => {
              const isMdApproved = mdApprovalStatus[params.data.id] === true;
              if (isMdApproved) {
                return { backgroundColor: "#e8f5e9" }; // Light green for MD approved
              }
              return {};
            }}
          />

          <TablePagination
            component="div"
            count={totalElements}
            page={paginationModel.page}
            onPageChange={onPageChange}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={paginationPageSizeSelector}
            ActionsComponent={ServerTablePaginationActions}
            sx={{
              "& .MuiTablePagination-input": {
                border: "1px solid #c7ccd1",
                borderRadius: "8px",
                px: 1,
                py: 0.25,
                mr: 1,
              },
              "& .MuiTablePagination-select": {
                fontWeight: 700,
              },
              "& .MuiTablePagination-selectIcon": {
                right: "4px",
              },
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default ActionHistoryList;
